import { Button, InputAdornment, ListItemIcon, Menu, MenuItem, styled, TextField } from '@mui/material';
import { CheckIcon, LayoutGridIcon, ListFilterIcon, ListIcon, SearchIcon } from 'lucide-react';
import { ChangeEvent, useState } from 'react';

const SORT_OPTIONS = [
  { value: 'name:asc', label: 'Title (A-Z)' },
  { value: 'name:desc', label: 'Title (Z-A)' },
  { value: 'updated_at:desc', label: 'Last modified' },
  { value: 'created_at:desc', label: 'Last created' },
];

interface StyledMenuItemProps {
  isActive: boolean;
}

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<StyledMenuItemProps>`
  display: flex;
  justify-content: space-between;
  background-color: ${(props) => (props.isActive ? '#F7FAFC' : 'transparent')};

  & > span {
    font-size: 14px;
    font-weight: ${(props) => (props.isActive ? 600 : 'normal')};
    color: #4a5568;
  }

  & .MuiListItemIcon-root {
    min-width: 24px;
    color: #4a5568;
  }
`;

interface BuildsToolbarProps {
  activeSort: string;
  viewOption: 'list' | 'grid';
  searchText: string;
  onSort: (sortBy: string) => void;
  onToggleView: () => void;
  onSearchChange: (searchText: string) => void;
}

function BuildsToolbar({
  activeSort,
  viewOption,
  searchText,
  onSort,
  onToggleView,
  onSearchChange,
}: BuildsToolbarProps) {
  const [sortByMenuAnchorEl, setSortByMenuAnchorEl] = useState<HTMLElement | null>(null);

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'space-between' }}>
      <TextField
        onChange={handleSearchInputChange}
        value={searchText}
        sx={{ width: 360 }}
        aria-label='Search builds'
        placeholder='Search'
        size='small'
        slotProps={{
          input: {
            sx: { borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' },
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon size={16} />
              </InputAdornment>
            ),
          },
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <Button sx={{ color: '#4a5568' }} onClick={onToggleView}>
          {viewOption === 'grid' ? <ListIcon /> : <LayoutGridIcon />}
          <span style={{ fontSize: '14px', marginLeft: '6px', color: '#718096' }}>
            {viewOption === 'grid' ? 'List' : 'Grid'}
          </span>
        </Button>
        <Button
          sx={{ color: '#4a5568' }}
          aria-haspopup='true'
          aria-expanded={sortByMenuAnchorEl ? 'true' : undefined}
          onClick={(event) => setSortByMenuAnchorEl(event.currentTarget)}
        >
          <ListFilterIcon style={{ fontSize: '14px' }} />
          <span style={{ fontSize: '14px', marginLeft: '6px', color: '#718096' }}>Sort by</span>
        </Button>
      </div>
      <Menu
        slotProps={{ paper: { style: { width: '24ch' } } }}
        anchorEl={sortByMenuAnchorEl}
        open={Boolean(sortByMenuAnchorEl)}
        onClose={() => setSortByMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {SORT_OPTIONS.map((option) => (
          <StyledMenuItem
            key={option.value}
            isActive={activeSort === option.value}
            onClick={() => {
              onSort(option.value);
              setSortByMenuAnchorEl(null);
            }}
          >
            <span>{option.label}</span>
            {activeSort === option.value && (
              <ListItemIcon>
                <CheckIcon />
              </ListItemIcon>
            )}
          </StyledMenuItem>
        ))}
      </Menu>
    </div>
  );
}

export default BuildsToolbar;
